import { defineStore } from 'pinia'
import { EyeV2Service } from '@/services/eye-v2-service'
import isUndefined from 'lodash/isUndefined'
import isNull from 'lodash/isNull'
import isNaN from 'lodash/isNaN'
import axios from 'axios'
import type { MarketInfo } from '@/services/eye-v2-service/types'
import type { ApiError, MarketType } from '@/utils/types'
import type { OhlcvTuple } from '@/utils/types'
import { FALLBACK_API_ERROR } from '@/utils/consts'

export const useEyeStore = defineStore('eye', {
  state: () => ({
    markets: {} as Record<string, Record<string, MarketInfo>>,
    ohlcv: {} as Record<string, OhlcvTuple[]>,
    spreads: {} as Record<string, Array<[number, number]> | OhlcvTuple[]>,
    loading: {} as Record<string, boolean>,
    error: {} as Record<string, ApiError | null>,
    lastRequestTime: {} as Record<string, number>,
  }),

  getters: {},

  actions: {
    async fetchMarkets(
      actionId: string,
      marketType: MarketType,
      asset: string,
      exs: string[],
      sellAsset: string | null,
      quote?: string,
    ) {
      this.loading[actionId] = true
      try {
        const req = await EyeV2Service.fetchMarketsInfo(
          marketType,
          asset,
          exs.join(','),
          sellAsset,
          quote,
        )
        this.markets[actionId] ??= {}
        req?.data.forEach((v) => {
          if (this.markets[actionId]) {
            this.markets[actionId][v.exchange] = v
          }
        })
        delete this.error[actionId]
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error[actionId] = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error[actionId] = FALLBACK_API_ERROR
        }
      } finally {
        this.loading[actionId] = false
      }
    },
    clearAction(actionId: string) {
      delete this.markets[actionId]
      delete this.ohlcv[actionId]
      delete this.loading[actionId]
      delete this.error[actionId]
    },
    clearActions() {
      this.markets = {}
      this.ohlcv = {}
      this.loading = {}
      this.error = {}
    },
    async fetchOhlcv(
      actionId: string,
      marketType: MarketType,
      asset: string,
      exchange: string,
      tf: string = '15m',
      quote?: string,
    ) {
      this.loading[actionId] = true
      try {
        const req = await EyeV2Service.fetchOhlcv(exchange, marketType, asset, tf, quote)
        this.ohlcv[actionId] = req?.data
        delete this.error[actionId]
        return req?.data
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error[actionId] = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error[actionId] = FALLBACK_API_ERROR
        }
      } finally {
        this.loading[actionId] = false
      }
    },
    calcOhlcvSpreads(
      a: OhlcvTuple[],
      b: OhlcvTuple[],
    ): Array<[timestamp: number, spreadPercent: number]> {
      if (!a.length || !b.length) return []
      const idxA = new Map(a.map((c, i) => [c[0], i]))
      const idxB = new Map(b.map((c, i) => [c[0], i]))
      const commonTs = [...idxA.keys()].filter((ts) => idxB.has(ts)).sort((x, y) => x - y)
      const result = commonTs.map((ts) => {
        const buy = a[idxA.get(ts)!]
        if (isUndefined(buy) || isNaN(buy) || isNull(buy)) {
          throw 'buy is null'
        }
        const sell = b[idxB.get(ts)!]
        if (isUndefined(sell) || isNaN(sell) || isNull(sell)) {
          throw 'sell is null'
        }
        const spread = Number(((sell[4] / buy[4] - 1) * 100).toFixed(2))
        return [ts, spread] as [number, number]
      })
      return result
    },
    async fetchMarketSpreads(
      actionId: string,
      marketType: string,
      asset: string,
      ex_buy: string,
      ex_sell: string,
      sellAsset?: string | null,
      quoteBuy?: string,
      quoteSell?: string,
    ) {
      if (
        this.spreads[actionId] &&
        this.lastRequestTime[actionId] &&
        +new Date() - this.lastRequestTime[actionId] <= 10000
      ) {
        return this.spreads[actionId]
      }
      if (this.loading[actionId]) {
        return null
      }
      this.loading[actionId] = true
      try {
        this.lastRequestTime[actionId] = +new Date()
        const req = await EyeV2Service.fetchOhlcv1m(
          ex_buy,
          ex_sell,
          asset,
          marketType,
          sellAsset,
          quoteBuy,
          quoteSell,
        )
        const result = this.calcOhlcvSpreads(req?.data[0] ?? [], req?.data[1] ?? [])
        this.spreads[actionId] = result
        delete this.error[actionId]
        return result
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error[actionId] = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error[actionId] = FALLBACK_API_ERROR
        }
      } finally {
        this.loading[actionId] = false
      }
    },
    async fetchMarketLimits(
      actionId: string,
      exBuy: string,
      exSell: string,
      asset: string,
      marketType: MarketType,
      sellAsset?: string | null,
      quoteBuy?: string,
      quoteSell?: string,
    ) {
      this.loading[actionId] = true
      try {
        const req = await EyeV2Service.fetchLimits(
          exBuy,
          exSell,
          asset,
          marketType,
          sellAsset,
          quoteBuy,
          quoteSell,
        )
        this.error[actionId] = null
        return req?.data
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error[actionId] = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error[actionId] = FALLBACK_API_ERROR
        }
      } finally {
        this.loading[actionId] = false
      }
    },

    async fetchSymbolTicks(actionId: string, exchange: string, symbol: string) {
      if (this.loading[actionId]) {
        return null
      }
      this.loading[actionId] = true
      try {
        this.lastRequestTime[actionId] = +new Date()
        const req = await EyeV2Service.fetchSymbolMarketTicks(exchange, symbol.replace('/', '_'))
        return req.data
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error[actionId] = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error[actionId] = FALLBACK_API_ERROR
        }
      } finally {
        this.loading[actionId] = false
      }
    },
  },
})
